import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AuthProvider, useAuth } from '@/lib/auth';
import { resolveBrowserCountry } from '@/lib/geo';
import { Toaster } from 'sonner';
import Landing from '@/pages/Landing';
import Partner from '@/pages/Partner';
import CompatibilityLanding from '@/pages/CompatibilityLanding';
import Login from '@/pages/Login';
import Signup from '@/pages/Signup';
import Verify from '@/pages/Verify';
import ForgotPassword from '@/pages/ForgotPassword';
import Onboarding from '@/pages/Onboarding';
import AppShell from '@/components/AppShell';
import Dashboard from '@/pages/Dashboard';
import Chat from '@/pages/Chat';
import PredictionDetail from '@/pages/PredictionDetail';
import Experts from '@/pages/Experts';
import ExpertDetail from '@/pages/ExpertDetail';
import Credits from '@/pages/Credits';
import Profile from '@/pages/Profile';
import Referrals from '@/pages/Referrals';
import Compatibility from '@/pages/Compatibility';
import AdminLogin from '@/pages/admin/AdminLogin';
import AdminShell from '@/pages/admin/AdminShell';
import AdminOverview from '@/pages/admin/AdminOverview';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminQuestions from '@/pages/admin/AdminQuestions';
import AdminPayments from '@/pages/admin/AdminPayments';
import AdminExperts from '@/pages/admin/AdminExperts';
import AdminConsultations from '@/pages/admin/AdminConsultations';
import AdminReferrals from '@/pages/admin/AdminReferrals';
import AdminKpFixtures from '@/pages/admin/AdminKpFixtures';
import AdminQuality from '@/pages/admin/AdminQuality';
import AdminPricing from '@/pages/admin/AdminPricing';
import AuthCallback from '@/pages/AuthCallback';
import '@/App.css';

function Protected({ children, requireBirth = false }) {
    const { user } = useAuth();
    if (!localStorage.getItem('ac_token')) return <Navigate to="/login" replace />;
    if (!user) return null;
    if (requireBirth && !user?.birth_details?.date) {
        return <Navigate to="/onboarding" replace />;
    }
    return children;
}

function AdminProtected({ children }) {
    const { user } = useAuth();
    const [slow, setSlow] = useState(false);
    useEffect(() => {
        // Round 37 — if user state is still null 15s after mount but a
        // token exists, something is wrong with the auth bootstrap
        // (`/auth/me` hung or returned a payload missing `is_admin`).
        // Surface a useful debug card instead of staring at a blank page.
        const t = setTimeout(() => setSlow(true), 15000);
        return () => clearTimeout(t);
    }, []);
    if (!localStorage.getItem('ac_token')) return <Navigate to="/admin/login" replace />;
    if (!user) {
        if (!slow) return null;
        return (
            <div className="min-h-screen flex items-center justify-center p-8 bg-obsidian">
                <div className="max-w-md text-center space-y-4">
                    <div className="font-serif text-2xl text-parchment">Admin session check is taking too long.</div>
                    <p className="text-sm text-slate-400">
                        We couldn't verify your admin account. Sign in again to refresh the session.
                    </p>
                    <button
                        onClick={() => { localStorage.removeItem('ac_token'); localStorage.removeItem('ac_user'); window.location.assign('/admin/login'); }}
                        className="px-5 py-2 bg-marigold text-obsidian rounded-sm font-medium"
                        data-testid="admin-stuck-relogin">
                        Sign in again
                    </button>
                </div>
            </div>
        );
    }
    if (!user.is_admin) return <Navigate to="/admin/login" replace />;
    return children;
}

// Round 32 — capture `?ref=CODE` into localStorage so it survives across
// route changes and the signup flow. Runs on every route change but only
// writes when the query-string carries a non-empty ref.
function ReferralCapture() {
    const location = useLocation();
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const ref = (params.get('ref') || '').trim().toUpperCase();
        if (ref) {
            localStorage.setItem('ac_ref', ref);
        }
    }, [location]);
    return null;
}

// A94 — Emergent Google OAuth callback detection. Runs SYNCHRONOUSLY
// during render (NOT in useEffect — that would race the AuthProvider's
// initial bootstrap and let /auth/me 401 before AuthCallback can
// exchange the session_id). When the URL fragment contains a fresh
// session_id, short-circuit normal routing and render the callback
// component to complete the exchange.
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
// URLS, THIS BREAKS THE AUTH.
function AppRoutes() {
    const location = useLocation();
    if (location.hash && location.hash.includes('session_id=')) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/partner" element={<Partner />} />
            <Route path="/compatibility" element={<CompatibilityLanding />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/forgot" element={<ForgotPassword />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/app" element={<Protected requireBirth><AppShell /></Protected>}>
                <Route index element={<Dashboard />} />
                <Route path="chat" element={<Chat />} />
                {/* Chat-pivot hard cutover: legacy /ask redirects to /chat */}
                <Route path="ask" element={<Navigate to="/app/chat" replace />} />
                <Route path="history" element={<Navigate to="/app" replace />} />
                <Route path="prediction/:id" element={<PredictionDetail />} />
                <Route path="consult" element={<Experts />} />
                <Route path="consult/:id" element={<ExpertDetail />} />
                <Route path="credits" element={<Credits />} />
                <Route path="profile" element={<Profile />} />
                <Route path="referrals" element={<Referrals />} />
                <Route path="compatibility" element={<Compatibility />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminProtected><AdminShell /></AdminProtected>}>
                <Route index element={<AdminOverview />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="questions" element={<AdminQuestions />} />
                <Route path="payments" element={<AdminPayments />} />
                <Route path="experts" element={<AdminExperts />} />
                <Route path="consultations" element={<AdminConsultations />} />
                <Route path="referrals" element={<AdminReferrals />} />
                <Route path="pricing" element={<AdminPricing />} />
                <Route path="quality" element={<AdminQuality />} />
                <Route path="kp-fixtures" element={<AdminKpFixtures />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}


export default function App() {
    // Round 40o.7 — fire the browser-side IP-geo lookup as early as
    // possible (independent of the auth flow). The result is cached in
    // localStorage and attached to every API request as
    // X-Detected-Country, becoming the primary signal for backend
    // region resolution. Bypasses Emergent's ingress, which strips
    // Cloudflare's cf-ipcountry header and was causing every signup
    // with a failing fallback to land on residence_country='IN'.
    useEffect(() => {
        resolveBrowserCountry();  // fire-and-forget; result lands in localStorage
    }, []);

    return (
        <AuthProvider>
            <BrowserRouter>
                <ReferralCapture />
                <div className="relative min-h-screen">
                    <div className="starfield" aria-hidden="true" />
                    <AppRoutes />
                    <Toaster
                        theme="dark"
                        toastOptions={{
                            style: {
                                background: '#121217',
                                color: '#F8FAFC',
                                border: '1px solid rgba(245, 158, 11, 0.3)',
                            },
                        }}
                    />
                </div>
            </BrowserRouter>
        </AuthProvider>
    );
}
